import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { supabase } from '../../lib/supabase';

export default function ContactPage() {
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    title: '先生',
    phone: '',
    email: '',
    taxId: '',
    businessStatus: '',
    consultationType: '',
    contactTime: '隨時皆可',
    message: '',
    agreeTerms: false
  });
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.agreeTerms) {
      alert('請同意服務條款與隱私權政策');
      return;
    }

    if (!formData.name || !formData.phone || !formData.businessStatus || !formData.consultationType) {
      alert('請填寫所有必填欄位');
      return;
    }

    if (formData.message.length > 500) {
      alert('需求說明不得超過 500 字');
      return;
    }

    setIsSubmitting(true);

    try {
      // 構建完整的訊息內容
      const fullMessage = `經營狀態：${formData.businessStatus}
諮詢項目：${formData.consultationType}
聯繫時段：${formData.contactTime}
公司統編：${formData.taxId}

${formData.message || '無'}`;

      // 直接存入 Supabase 資料庫
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            name: `${formData.name} ${formData.title}`,
            phone: formData.phone,
            email: formData.email || '未提供',
            message: fullMessage
          }
        ]);

      if (error) throw error;

      setSubmitSuccess(true);
      setFormData({
        name: '',
        title: '先生',
        phone: '',
        email: '',
        taxId: '',
        businessStatus: '',
        consultationType: '',
        contactTime: '隨時皆可',
        message: '',
        agreeTerms: false
      });
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      console.error('提交失敗:', error);
      alert('送出失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: '諮詢需要收費嗎？',
      answer: '初步諮詢與評估完全免費，僅於確認委任並正式簽署合約後，才會依案件難度收取服務費。'
    },
    {
      question: '信用有瑕疵可以辦理嗎？',
      answer: '視程度而定。輕微遲繳仍有機會爭取，嚴重信用不良則機率極低。建議先由顧問進行免費信用健檢評估。'
    },
    {
      question: '保證一定過件嗎？',
      answer: `合法的顧問公司不會承諾「保證過件」，但我們會透過完整的事前評估與專業規劃，
大幅提高過件率，並協助爭取相對較好的條件。`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar scrolled={scrolled} />
      
      <div className="pt-32 pb-16 bg-gradient-to-br from-black to-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h1 className="text-5xl font-bold text-white mb-6">預約免費諮詢</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            專業顧問團隊為您提供企業貸款與青年創業的完整諮詢服務
          </p>
        </div>
      </div>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            
            {/* 左側：公司資訊區 */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">公司資訊</h2>
              
              {/* 聯絡資訊 */}
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FF8C42]/10 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-[#FF8C42]"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">公司地址</h3>
                    <p className="text-gray-700 mb-2">台北市內湖區行善路385號</p>
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=台北市內湖區行善路385號" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-black hover:text-[#FF8C42] text-sm font-medium transition-colors cursor-pointer"
                    >
                      在 Google 地圖上查看
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FF8C42]/10 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-[#FF8C42]"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">聯絡電話</h3>
                    <p className="text-gray-900 font-semibold mb-1">02-27951331</p>
                    <p className="text-gray-600 text-sm">週一至週五 10:00 - 17:30</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#FF8C42]/10 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-[#FF8C42]"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-2">電子郵件</h3>
                    <p className="text-gray-700 mb-3">contact@psfcapital.tw</p>
                    
                    {/* LINE 官方帳號按鈕 */}
                    <a
                      href="https://line.me/R/ti/p/@678gwbht"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-3 bg-[#06C755] text-white rounded-lg hover:bg-[#05b34c] transition-all font-medium text-sm whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg"
                    >
                      <i className="ri-line-fill text-xl"></i>
                      加入 LINE 好友
                    </a>
                  </div>
                </div>
              </div>

              {/* 諮詢流程說明 */}
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-black"></i>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">諮詢流程說明</h2>
                </div>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center font-bold">
                        1
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">送出表單</h3>
                      <p className="text-gray-600 text-sm">填寫右方資訊，越詳細越有助於評估</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center font-bold">
                        2
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">專人聯繫</h3>
                      <p className="text-gray-600 text-sm">顧問將於 1 個工作日內回覆，進行初步訪談</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center font-bold">
                        3
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">安排面談</h3>
                      <p className="text-gray-600 text-sm">確認需求後，安排實體或視訊會議詳談</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 常見問題快速導覽 */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">常見問題快速導覽</h2>
                
                <div className="space-y-3 mb-6">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                        className="w-full px-5 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
                      >
                        <span className="font-semibold text-gray-900 text-sm">{faq.question}</span>
                        <i className={`ri-arrow-${expandedFaq === index ? 'up' : 'down'}-s-line text-xl text-gray-500 flex-shrink-0`}></i>
                      </button>
                      {expandedFaq === index && (
                        <div className="px-5 py-4 bg-gray-50 border-t border-gray-200">
                          <p className="text-gray-700 text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <a
                  href="/faq"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-black text-black rounded-lg hover:bg-black hover:text-white transition-all font-medium text-sm whitespace-nowrap cursor-pointer"
                >
                  查看完整常見問題
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            {/* 右側：表單區 */}
            <div>
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">預約免費諮詢</h2>
                <p className="text-gray-600 mb-8">請填寫以下資訊,標示 <span className="text-red-500">*</span> 為必填欄位</p>

                {submitSuccess && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-2xl text-green-600 flex-shrink-0"></i>
                    <div>
                      <p className="font-semibold text-green-900 mb-1">送出成功！</p>
                      <p className="text-sm text-green-700">我們已收到您的諮詢需求,顧問將於 1 個工作日內與您聯繫。</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} data-readdy-form id="consultation-form">
                  {/* 聯絡人姓名 + 稱謂 */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="col-span-2">
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        聯絡人姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="王小明"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-2">
                        稱謂
                      </label>
                      <div className="flex gap-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="title"
                            value="先生"
                            checked={formData.title === '先生'}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            className="w-4 h-4 text-black cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">先生</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="title"
                            value="小姐"
                            checked={formData.title === '小姐'}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            className="w-4 h-4 text-black cursor-pointer"
                          />
                          <span className="text-sm text-gray-700">小姐</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* 聯絡電話 */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      聯絡電話 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="0912-345-678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                      required
                    />
                  </div>

                  {/* 電子信箱 */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      電子信箱
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="example@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                    />
                  </div>

                  {/* 公司統編 */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      公司統編 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="taxId"
                      required
                      value={formData.taxId}
                      onChange={(e) => setFormData({...formData, taxId: e.target.value})}
                      placeholder="12345678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm"
                    />
                  </div>

                  {/* 經營狀態 */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      經營狀態 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="businessStatus"
                      value={formData.businessStatus}
                      onChange={(e) => setFormData({...formData, businessStatus: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm cursor-pointer"
                      required
                    >
                      <option value="">請選擇經營狀態</option>
                      <option value="創業未滿1年">創業未滿 1 年</option>
                      <option value="公司成立1-3年">公司成立 1–3 年</option>
                      <option value="公司成立3年以上">公司成立 3 年以上</option>
                    </select>
                  </div>

                  {/* 諮詢項目 */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      諮詢項目 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="consultationType"
                      value={formData.consultationType}
                      onChange={(e) => setFormData({...formData, consultationType: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm cursor-pointer"
                      required
                    >
                      <option value="">請選擇諮詢項目</option>
                      <option value="青年創業貸款">青年創業貸款</option>
                      <option value="中小企業融資">中小企業融資</option>
                      <option value="信保基金專案">信保基金專案</option>
                      <option value="補助/設備升級">補助 / 設備升級</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  {/* 方便聯繫時段 */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      方便聯繫時段
                    </label>
                    <select
                      name="contactTime"
                      value={formData.contactTime}
                      onChange={(e) => setFormData({...formData, contactTime: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm cursor-pointer"
                    >
                      <option value="隨時皆可">隨時皆可</option>
                      <option value="平日白天">平日白天</option>
                      <option value="平日晚上">平日晚上</option>
                    </select>
                  </div>

                  {/* 需求說明 */}
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      需求說明或留言
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({...formData, message: e.target.value});
                        }
                      }}
                      placeholder="請簡述您的產業類型、目前遇到的資金問題，或是想諮詢的具體事項…"
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent text-sm resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-2 text-right">{formData.message.length} / 500 字</p>
                  </div>

                  {/* 個資同意 */}
                  <div className="mb-8">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="agreeTerms"
                        checked={formData.agreeTerms}
                        onChange={(e) => setFormData({...formData, agreeTerms: e.target.checked})}
                        className="w-5 h-5 text-black rounded border-gray-300 focus:ring-2 focus:ring-black mt-0.5 cursor-pointer"
                        required
                      />
                      <span className="text-sm text-gray-700 leading-relaxed">
                        我同意服務條款與隱私權政策，並授權蒐集、處理及利用我的個人資料，用於聯繫與服務提供。<span className="text-red-500">*</span>
                      </span>
                    </label>
                  </div>

                  {/* 送出按鈕 */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#FF8C42] to-[#FF6B35] text-white py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        送出中...
                      </>
                    ) : (
                      <>
                        確認送出
                        <i className="ri-send-plane-fill"></i>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 右下角浮動 LINE 客服按鈕 */}
      <a
        href="https://line.me/R/ti/p/@678gwbht"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-28 right-6 z-40 w-14 h-14 bg-[#06C755] rounded-full flex items-center justify-center text-white shadow-lg hover:scale-110 transition-transform duration-300 group"
        aria-label="LINE 即時諮詢"
      >
        <i className="ri-line-fill text-2xl"></i>
        <span className="absolute right-16 bg-gray-900 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          LINE 即時諮詢
        </span>
      </a>

      <Footer />
    </div>
  );
}